import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMusic } from './hooks/useMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL, WS_URL } from './config/apiConfig';
import { getWebSocketAuthMessage } from './config/apiConfig';
import { needsTokenRefresh, refreshToken, isTokenValid } from './auth';
import CustomDropdown from './CustomDropdown';
import { useContainerScale } from './hooks/useViewportScale';
import './Autobattler.css';

function Autobattler({ user, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  useMusic('/music/Epic_1.mp3');
  
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState(
    localStorage.getItem('selectedPveStrategy') || localStorage.getItem('selectedStrategy') || ''
  );
  const [factionTranslations, setFactionTranslations] = useState({});
  const [strategyValidations, setStrategyValidations] = useState({});
  const [ws, setWs] = useState(null);
  
  const userId = typeof user === 'string' ? user : (user?.id || user?.username);
  
  // Faction colors for orbs
  const factionColors = {
    'Remnants of Humanity': '#556B2F',
    'Exolot Dominion': '#4B0082',
    'League of Shadows': '#1A1A1A',
    'Ra\'s Children': '#F4A460',
    'Asrah': '#8B0000',
    'The Sentients': '#ADD8E6',
    'Combined': '#FFA500'
  };

  // Get faction orb background color/gradient
  const getFactionOrbBackground = (factionName) => {
    if (!factionName || factionName === 'Unknown') {
      return '#ccc';
    }

    // Check if it's a combined faction and return gradient
    if (factionName === 'The Conspiracy') {
      return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
    } else if (factionName === 'The Corrupted Core') {
      return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
    } else if (factionName === 'The Covenant') {
      return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
    } else if (factionName === 'The Divine Syndicate') {
      return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
    } else if (factionName === 'The Forsaken Pact') {
      return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
    } else if (factionName === 'The Hollow') {
      return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
    } else if (factionName === 'The Iron Alliance') {
      return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
    } else if (factionName === 'The Last Crusade') {
      return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
    } else if (factionName === 'The Shadow Consortium') {
      return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
    } else if (factionName === 'The Silent Accord') {
      return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
    } else if (factionName === 'The Unholy Union') {
      return 'linear-gradient(to right, #8B0000 50%, #4B0082 50%)';
    } else if (factionName === 'The Vengeful Alliance') {
      return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
    } else if (factionName === 'The Warlord Coalition') {
      return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
    } else if (factionName === 'The Wicked Covenant') {
      return 'linear-gradient(to right, #1A1A1A 50%, #8B0000 50%)';
    } else if (factionName === 'The Wrathful Pact') {
      return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
    }

    return factionColors[factionName] || '#ccc';
  };

  // Fetch strategies
  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No token found');
          return;
        }

        // Check if token needs refresh
        if (needsTokenRefresh(token)) {
          const refreshed = await refreshToken();
          if (!refreshed) {
            console.error('Failed to refresh token');
            return;
          }
        }

        const response = await fetch(`${API_URL}/strategies`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch strategies');
        }

        const data = await response.json();
        setStrategies(data);

        // Fetch faction translations
        const factionResponse = await fetch(`${API_URL}/factions`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (factionResponse.ok) {
          const factionData = await factionResponse.json();
          const translations = {};
          factionData.forEach(faction => {
            translations[faction.name] = faction.name_translated || faction.name;
          });
          setFactionTranslations(translations);
        }
      } catch (error) {
        console.error('Error fetching strategies:', error);
      }
    };

    fetchStrategies();
  }, []);

  // Validate strategies
  useEffect(() => {
    const validateStrategies = async () => {
      if (strategies.length === 0) return;

      const validations = {};
      for (const strategy of strategies) {
        try {
          const token = localStorage.getItem('token');
          if (!token) continue;

          if (needsTokenRefresh(token)) {
            const refreshed = await refreshToken();
            if (!refreshed) continue;
          }

          const response = await fetch(`${API_URL}/strategies/${strategy.id}/validate`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            validations[strategy.id] = data;
          }
        } catch (error) {
          console.error(`Error validating strategy ${strategy.id}:`, error);
        }
      }

      setStrategyValidations(validations);
    };

    validateStrategies();
  }, [strategies]);

  const handleStartAutobattler = () => {
    if (ws) return;
    if (!selectedStrategyId) {
      alert(t('autobattler.selectStrategy') || 'Please select a strategy');
      return;
    }

    // Validate strategy before starting
    const validation = strategyValidations[selectedStrategyId];
    if (!validation || !validation.valid) {
      const insufficientOrders = validation?.insufficientOrders || [];
      if (insufficientOrders.length > 0) {
        const orderList = insufficientOrders.map(o => `${o.name} (need ${o.required}, have ${o.owned})`).join(', ');
        alert(`Cannot start autobattler. This strategy contains orders you don't own enough copies of: ${orderList}`);
      } else {
        alert('Cannot start autobattler. This strategy contains orders you don\'t own.');
      }
      return;
    }

    playSoundEffect('/sounds/click.mp3');

    const socket = new window.WebSocket(WS_URL);
    setWs(socket);

    socket.onopen = () => {
      const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
      const token = localStorage.getItem('token');
      socket.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'authenticated') {
        // Send autobattler start message
        socket.send(JSON.stringify({
          type: 'autobattler-start',
          strategyId: parseInt(selectedStrategyId)
        }));
      } else if (data.type === 'match-found') {
        // Navigate to battle
        navigate('/battle', {
          state: {
            gameId: data.gameId,
            gameState: data.gameState,
            userId: userId
          }
        });
        socket.close();
        setWs(null);
      } else if (data.type === 'error') {
        alert(data.message || 'Failed to start autobattler game');
        socket.close();
        setWs(null);
      }
    };

    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
      alert('Connection error. Please try again.');
      socket.close();
      setWs(null);
    };

    socket.onclose = () => {
      setWs(null);
    };
  };

  return (
    <div className={`autobattler-container${isContainerMobile ? ' mobile' : ''}`} style={{
      width: `${containerBaseWidth}px`,
      height: `${containerBaseHeight}px`,
      transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
      transformOrigin: 'top left'
    }}>
      <div className="autobattler-header">
        <h2>{t('autobattler.title') || 'Autobattler'}</h2>
        <p className="autobattler-description">
          {t('autobattler.description') || 'Watch your strategy battle against an AI opponent. Both sides are controlled by bots.'}
        </p>
      </div>

      <div className="autobattler-content">
        <div className="strategy-selector-container">
          <label className="strategy-selector-label">
            {t('autobattler.selectStrategy') || 'Select Strategy'}
          </label>
          {strategies.length === 0 ? (
            <div className="strategy-selector-empty">
              {t('autobattler.noStrategiesAvailable') || 'No strategies available'}
            </div>
          ) : (
            <div className="strategy-selector-dropdown-wrapper">
              <CustomDropdown
                options={strategies.map(strategy => ({
                  value: strategy.id.toString(),
                  label: strategy.name,
                  faction_name: strategy.faction_name
                }))}
                value={selectedStrategyId ? selectedStrategyId.toString() : ''}
                onChange={(strategyId) => {
                  const id = strategyId ? parseInt(strategyId) : null;
                  setSelectedStrategyId(id);
                  if (id) {
                    localStorage.setItem('selectedPveStrategy', id.toString());
                  } else {
                    localStorage.removeItem('selectedPveStrategy');
                  }
                }}
                placeholder={t('autobattler.selectStrategyPlaceholder') || 'Select a strategy...'}
                openUpward={true}
                renderOption={(option) => {
                  const strategy = strategies.find(s => s.id.toString() === option.value);
                  const factionName = strategy?.faction_name || option.faction_name;
                  if (!factionName) {
                    return <span>{option.label}</span>;
                  }
                  const translatedFactionName = strategy?.faction_name_translated || factionTranslations[factionName] || factionName;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        className="faction-orb"
                        style={{ background: getFactionOrbBackground(factionName) }}
                        title={translatedFactionName}
                      />
                      <span>{option.label}</span>
                    </div>
                  );
                }}
              />
            </div>
          )}
        </div>

        <button
          className="autobattler-start-button"
          onClick={handleStartAutobattler}
          disabled={!selectedStrategyId || !!ws}
        >
          {ws ? (t('autobattler.starting') || 'Starting...') : (t('autobattler.start') || 'Start Autobattler')}
        </button>
      </div>
    </div>
  );
}

export default Autobattler;

